import { Button, notification, Steps } from "antd";
import cuid from "cuid";
import dayjs from "dayjs";
import { Formik } from "formik";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiChevronLeft, BiChevronRight, BiSave } from "react-icons/bi";
import Header from "shared/components/Header";
import { NAME_SPACES } from "shared/locales/constants";
import { INSURANCE_PLAN_RISKS, STATUS_PUBLICATION } from "utils/constants";
import { rulesToSlots } from "./Components/eligibility-dimensions";
import ConfigureBenefits from "./Steps/ConfigureBenefits";
import PlanDetails from "./Steps/PlanDetails";
import ReviewSummary from "./Steps/ReviewSummary";
import SelectCoverages from "./Steps/SelectCoverages";
import { stepValidations } from "./validation";

const buildInitialValues = (data) => ({
  name: get(data, "name", ""),
  identifier: get(data, "identifier", ""),
  description: get(data, "description", ""),
  risk: get(data, "risk", INSURANCE_PLAN_RISKS.HEALTH),
  status: get(data, "status", STATUS_PUBLICATION.ACTIVE),
  category: get(data, "category", ""),
  level: get(data, "level", null),
  startDate: get(data, "startDate") || dayjs().toISOString(),
  endDate: get(data, "endDate") || dayjs().add(1, "year").toISOString(),
  params: (get(data, "params") || []).map((param) => ({
    id: param.id,
    rules: rulesToSlots(get(param, "rules")),
  })),
  zoneType: get(data, "zoneType"),
  zones: get(data, "zones") || [],
  documentTemplates: (get(data, "documentTemplates") || []).map((template) => ({
    id: cuid(),
    templateId: get(template, "templateId"),
    type: get(template, "type"),
    area: get(template, "area"),
    outputName: get(template, "outputName"),
    conditions: get(template, "conditions") || {
      operator: "AND",
      rules: [],
    },
  })),
  config: get(data, "config") || {},
  ageBrackets: get(data, "ageBrackets") || [],
  regimes: get(data, "regimes") || [],
  insurancePlanCoverages: (get(data, "insurancePlanCoverages") || []).map(
    (coverage) => ({
      id: coverage.id,
      coverageCategory: coverage.coverageCategory,
      needs: get(coverage, "needs") || { level: 1 },
      pricer: coverage.pricer,
      coverageMappings: (get(coverage, "coverageMappings") || []).map(
        (mapping) => ({
          id: mapping.id,
          waitingDays: get(mapping, "waitingDays", 0),
          carenceDays: get(mapping, "carenceDays"),
          coverage: mapping.coverage,
          limits: get(mapping, "limits") || [],
          supportingInformation: get(mapping, "supportingInformation") || [],
          pricer: mapping.pricer,
        }),
      ),
    }),
  ),
});

const buildPlanPayload = (values) => ({
  name: values.name,
  identifier: values.identifier,
  description: values.description,
  risk: values.risk,
  status: values.status,
  category: values.category,
  level: values.level,
  startDate: values.startDate,
  endDate: values.endDate,
});

const buildCoveragesPayload = (values) => ({
  insurancePlanCoverages: (values.insurancePlanCoverages || []).map((cov) => ({
    id: cov.id,
    coverageCategory: { id: get(cov, "coverageCategory.id") },
    needs: get(cov, "needs.id")
      ? { id: cov.needs.id, level: cov.needs.level }
      : undefined,
    coverageMappings: (get(cov, "coverageMappings") || []).map((mapping) => ({
      id: mapping.id,
      waitingDays: mapping.waitingDays,
      coverage: { id: get(mapping, "coverage.id") },
      limits: (get(mapping, "limits") || []).map((limit) => ({
        id: limit.id,
        code: limit.code,
        value: limit.value,
      })),
    })),
  })),
});

const Configure = ({ data, updateInsurancePlan, cancel, refetch }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const steps = [
    { title: t("CONFIGURE.STEPS.PLAN_DETAILS") },
    { title: t("CONFIGURE.STEPS.SELECT_COVERAGES") },
    { title: t("CONFIGURE.STEPS.CONFIGURE_BENEFITS") },
    { title: t("CONFIGURE.STEPS.REVIEW") },
  ];

  const saveProgress = async (values) => {
    const where = { id: data.id };
    await updateInsurancePlan({
      variables: { where, data: buildPlanPayload(values) },
    });
    if ((values.insurancePlanCoverages || []).length > 0) {
      await updateInsurancePlan({
        variables: { where, data: buildCoveragesPayload(values) },
      });
    }
  };

  const handleNext = async (values) => {
    const validation = stepValidations[currentStep];
    if (validation) {
      try {
        await validation.validate(values, { abortEarly: false });
      } catch (err) {
        const errors = get(err, "errors", []);
        notification.warning({
          message: t("CONFIGURE.NOTIFICATIONS.VALIDATION"),
          description:
            errors[0] || t("CONFIGURE.NOTIFICATIONS.VALIDATION_REQUIRED"),
        });
        return;
      }
    }

    setSaving(true);
    try {
      await saveProgress(values);
      if (refetch) await refetch();
      notification.success({
        message: t("CONFIGURE.NOTIFICATIONS.PROGRESS_SAVED"),
      });
      setCurrentStep((prev) => prev + 1);
    } catch (err) {
      notification.error({
        message: t("CONFIGURE.NOTIFICATIONS.SAVE_FAILED"),
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePrevious = () => setCurrentStep((prev) => prev - 1);

  const handleFinalSave = async (values) => {
    setSaving(true);
    try {
      await saveProgress(values);
      notification.success({
        message: t("CONFIGURE.NOTIFICATIONS.CONFIG_SAVED"),
      });
    } catch (err) {
      notification.error({
        message: t("CONFIGURE.NOTIFICATIONS.SAVE_FAILED"),
      });
    } finally {
      setSaving(false);
    }
  };

  const renderStep = (formikProps) => {
    const { values, setFieldValue, errors, touched, handleChange } =
      formikProps;
    const stepProps = {
      values,
      setFieldValue,
      errors,
      touched,
      refetch,
      handleChange,
      data,
    };
    switch (currentStep) {
      case 0:
        return <PlanDetails {...stepProps} />;
      case 1:
        return <SelectCoverages {...stepProps} />;
      case 2:
        return <ConfigureBenefits {...stepProps} />;
      case 3:
        return <ReviewSummary {...stepProps} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Header
        key="header"
        title={t("CONFIGURE.HEADER.TITLE", { name: data.name })}
        actions={[
          {
            text: t("CONFIGURE.HEADER.CANCEL"),
            onClick: cancel,
            type: "primary--outlined",
            icon: <BiChevronLeft size={20} />,
          },
        ]}
      />
      <div className="configure-wizard">
        <Steps
          current={currentStep}
          items={steps}
          className="m-bottom-24"
          onChange={(step) => {
            if (step < currentStep) setCurrentStep(step);
          }}
        />
        <Formik
          initialValues={buildInitialValues(data)}
          onSubmit={handleFinalSave}
          enableReinitialize
        >
          {(formikProps) => (
            <div>
              <div className="configure-wizard__content">
                {renderStep(formikProps)}
              </div>
              <div className="configure-wizard__actions d-flex justify--between m-top-24">
                <div>
                  {currentStep > 0 && (
                    <Button
                      type="default"
                      size="large"
                      onClick={handlePrevious}
                      icon={<BiChevronLeft size={18} />}
                    >
                      {t("CONFIGURE.HEADER.PREVIOUS")}
                    </Button>
                  )}
                </div>
                <div>
                  {currentStep < steps.length - 1 ? (
                    <Button
                      type="primary"
                      size="large"
                      loading={saving}
                      onClick={() => handleNext(formikProps.values)}
                    >
                      {t("CONFIGURE.HEADER.SAVE_NEXT")}
                      <BiChevronRight size={18} />
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      size="large"
                      loading={saving}
                      onClick={() => handleFinalSave(formikProps.values)}
                      icon={<BiSave size={18} />}
                    >
                      {t("CONFIGURE.HEADER.SAVE_CONFIG")}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </Formik>
      </div>
    </>
  );
};

export default Configure;
